import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { SpanishTooltip } from '../../components/charts/SpanishTooltip';
import type { IndicatorMeasurement } from './types';
import { formatMeasurementPeriodDisplay } from './objectiveUtils';

interface IndicatorChartProps {
  measurements: IndicatorMeasurement[];
  targetValue?: number | null;
  lowerLimit?: number | null;
  upperLimit?: number | null;
}

export function IndicatorChart({
  measurements,
  targetValue,
  lowerLimit,
  upperLimit,
}: IndicatorChartProps) {
  const data = measurements.map((m) => ({
    period: formatMeasurementPeriodDisplay(m.periodLabel),
    value: m.value,
    withinTarget: m.withinTarget,
  }));

  if (data.length === 0) {
    return (
      <div
        data-testid="indicator-chart-empty"
        className="flex items-center justify-center h-[240px] text-sm text-muted-foreground border border-border rounded-lg"
      >
        Sin mediciones registradas
      </div>
    );
  }

  return (
    <div data-testid="indicator-chart" className="h-[280px] w-full">
      <ul className="sr-only" data-testid="indicator-chart-periods" aria-label="Períodos">
        {data.map((point) => (
          <li key={point.period}>{point.period}</li>
        ))}
      </ul>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" />
          <YAxis />
          <SpanishTooltip />
          {targetValue != null && (
            <ReferenceLine y={targetValue} stroke="#10b981" strokeDasharray="4 4" />
          )}
          {lowerLimit != null && (
            <ReferenceLine y={lowerLimit} stroke="#f59e0b" strokeDasharray="2 2" />
          )}
          {upperLimit != null && (
            <ReferenceLine y={upperLimit} stroke="#f59e0b" strokeDasharray="2 2" />
          )}
          <Line
            type="monotone"
            dataKey="value"
            name="Valor medido"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
